import React, { useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { CheckCircle } from "@phosphor-icons/react";

const empty = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  country: "",
  profile_type: "",
  interest_type: "",
  note: "",
};

export default function WaitlistForm({ prefill }) {
  const { t } = useI18n();
  const w = t.waitlist;
  const [form, setForm] = useState({ ...empty, ...(prefill || {}) });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || !form.email.trim() || !form.profile_type || !form.interest_type) {
      setError(w.errorRequired);
      return;
    }
    setStatus("submitting");
    try {
      await api.post("/waitlist", form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err.response?.data?.detail) || w.errorGeneric);
    }
  };

  if (status === "success") {
    return (
      <div data-testid="waitlist-success" className="border border-brass bg-ivory p-12 md:p-16 text-center flex flex-col items-center">
        <CheckCircle size={48} weight="thin" className="text-brass mb-6" />
        <h3 className="font-headings text-3xl text-charcoal mb-4">{w.success}</h3>
        <p className="font-body text-charcoal/60 max-w-md mb-8">{w.successBody}</p>
        <button
          data-testid="waitlist-again"
          onClick={() => {
            setForm({ ...empty });
            setStatus("idle");
          }}
          className="font-mono text-[0.7rem] uppercase tracking-widest text-charcoal/60 hover:text-brass transition-colors border-b border-charcoal/30 pb-1"
        >
          {w.again}
        </button>
      </div>
    );
  }

  const labelCls = "text-[0.7rem] font-mono uppercase tracking-widest text-charcoal/60 mb-2 block";
  const inputCls =
    "w-full border-b border-charcoal/20 bg-transparent py-3 px-0 rounded-none focus:outline-none focus:border-brass transition-colors font-body text-lg placeholder:text-charcoal/30";
  const selectCls = inputCls + " appearance-none cursor-pointer";

  return (
    <form data-testid="waitlist-form" onSubmit={submit} className="border border-habitus-border bg-ivory p-8 md:p-12 lg:p-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
        <div className="md:col-span-2">
          <label className={labelCls}>{w.labels.full_name} *</label>
          <input data-testid="field-full_name" className={inputCls} value={form.full_name} onChange={set("full_name")} />
        </div>
        <div>
          <label className={labelCls}>{w.labels.email} *</label>
          <input data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <label className={labelCls}>{w.labels.phone}</label>
          <input data-testid="field-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <label className={labelCls}>{w.labels.city}</label>
          <input data-testid="field-city" className={inputCls} value={form.city} onChange={set("city")} />
        </div>
        <div>
          <label className={labelCls}>{w.labels.country}</label>
          <input data-testid="field-country" className={inputCls} value={form.country} onChange={set("country")} />
        </div>
        <div>
          <label className={labelCls}>{w.labels.profile_type} *</label>
          <select data-testid="field-profile_type" className={selectCls} value={form.profile_type} onChange={set("profile_type")}>
            <option value="">{w.placeholders.select}</option>
            {w.profileOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>{w.labels.interest_type} *</label>
          <select data-testid="field-interest_type" className={selectCls} value={form.interest_type} onChange={set("interest_type")}>
            <option value="">{w.placeholders.select}</option>
            {w.interestOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>{w.labels.note}</label>
          <textarea
            data-testid="field-note"
            rows={3}
            className={inputCls + " resize-none"}
            placeholder={w.placeholders.note}
            value={form.note}
            onChange={set("note")}
          />
        </div>
      </div>

      {error && (
        <p data-testid="waitlist-error" className="mt-8 font-mono text-xs uppercase tracking-widest text-red-700">
          {error}
        </p>
      )}

      <button
        data-testid="waitlist-submit"
        type="submit"
        disabled={status === "submitting"}
        className="mt-10 bg-charcoal text-ivory hover:bg-secondary transition-colors duration-300 px-10 py-4 font-mono uppercase tracking-widest text-xs disabled:opacity-50"
      >
        {status === "submitting" ? w.submitting : w.submit}
      </button>
    </form>
  );
}
